import * as posActions from "../../lib/posActions";
import * as routeHelpers from "../../lib/routeHelpers";
import * as tillEndpoints from "../../lib/api/till";
import cn from "classnames";
import describeNetworkError from "../../lib/describeNetworkError";
import Drawer from "../Drawer";
import ErrorModal from "../ErrorModal";
import logger from "../../lib/sentryLogger";
import ManagerHeader from "../ManagerHeader";
import ManagerPermissionForm from "../ManagerPermissionForm";
import OutlineButton from "../OutlineButton";
import React, {useEffect, useMemo, useRef, useState} from "react";
import Spinner from "../Spinner";
import styles from "./TillAssignment.module.css";
import tillIcon from "../../images/till.svg";
import ToggleButton from "../ToggleButton";
import usePrevious from "../../lib/usePrevious";
import {Actions} from "habit-core";
import {useAppDispatch, useAppSelector} from "../../lib/hooks";
import {useNavigate} from "react-router-dom";
import {ValidateManagerResponse} from "../../lib/api/users";

const strings = {
    title: "Till Assignment",
    mainInstruction: "Assign Till To Register",
    instructions:
        "Please select the assigned till number and connect that till to this register. When completed, tap the button below.",
    noticeTitle: "Important Notice:",
    noticeInstruction:
        "Ensure that there is a total of $300 in cash in the till.",
    assign: "Assign Till",
    loadAssignmentsErrorMessage:
        "Failed to load the list of currently assigned tills.",
    retry: "Retry",
    refreshTills: "Refresh Available Tills",
    skipAssignmentDriveThru: "Drive-thru Order Taker",
    skipAssignmentLineBuster: "Line Buster",
    stationModeChangeError:
        "An unexpected error occurred when recording the station mode change. Please try again.",
    modesWithoutTill: "Modes Without a Till",
    noTillOverride: "No Till Override",
};

export default function TillAssignment() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const assignedTill = useAppSelector(
        (state) => state.pos.station.assignedTill,
    );
    const trainingModeAssignedTill = useAppSelector(
        (state) => state.pos.station.trainingModeAssignedTill,
    );
    const tillAssignmentSkipped = useAppSelector(
        (state) => state.pos.station.tillAssignmentSkipped,
    );
    const isAssigningTill = useAppSelector(
        (state) => state.pos.station.isAssigningTill,
    );
    const tillAssignmentErrorMessage = useAppSelector(
        (state) => state.pos.errors.tillAssignmentErrorMessage,
    );

    const prevAssignedTill = usePrevious(assignedTill);
    const prevTrainingAssignedTill = usePrevious(trainingModeAssignedTill);
    const isTraining = useAppSelector((state) => state.pos.station.isTraining);
    const isLab = useAppSelector((state) => state.pos.store.isLab);
    const stationMode = useAppSelector((state) => state.pos.station.mode);
    const deviceId = useAppSelector((state) => state.pos.deviceId);
    const defaultCategoryId = useAppSelector(
        (state) =>
            state.menus.byId[state.currentOrder.menuId ?? ""]
                ?.defaultCategoryId,
    );
    const menusById = useAppSelector((state) => state.menus.byId);
    const driveThruMenuDefaultCategoryId = useMemo(() => {
        const driveThruMenuId = Object.keys(menusById).find(
            (menuId) =>
                menusById[menuId].type === "lunch_and_dinner_drive_thru",
        );
        return driveThruMenuId
            ? menusById[driveThruMenuId]?.defaultCategoryId
            : undefined;
    }, [menusById]);
    const posCurrentOrder = useAppSelector((state) => state.pos.currentOrder);

    const [tillToAssign, setTillToAssign] = useState(-1);
    const [numTills, setNumTills] = useState(0);
    const [unavailableTills, setUnavailableTills] = useState<number[]>([]);
    const [loadAssignmentsErrored, setLoadAssignmentsErrored] = useState(false);
    const noTillSelection = useRef<
        "drive-thru" | "line_buster" | "no-till-override"
    >();
    const [managerPermissionsDrawerOpen, setManagerPermissionsDrawerOpen] =
        useState(false);
    const [stationModeChangeErrored, setStationModeChangeErrored] =
        useState(false);

    const loadCurrentAssignments = () => {
        setLoadAssignmentsErrored(false);
        setLoading(true);
        tillEndpoints
            .getCurrentAssignments()
            .then((currentAssignments) => {
                setNumTills(currentAssignments.numTills);
                setUnavailableTills(
                    currentAssignments.assignedTills.map((a) => a.tillNumber),
                );
                const assignmentWithDeviceId = deviceId
                    ? currentAssignments.assignedTills.find(
                          (a) => a.deviceId === deviceId,
                      )
                    : undefined;
                if (assignmentWithDeviceId) {
                    dispatch(
                        posActions.assignTill(
                            assignmentWithDeviceId.tillNumber,
                            true,
                        ),
                    );
                }
            })
            .catch((err) => {
                logger.error("Failed to get current till assignments.", {
                    errorMessage: describeNetworkError(err).join("\n"),
                });
                setLoadAssignmentsErrored(true);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const [clearTillsErrorMessage, setClearTillsErrorMessage] = useState("");
    const clearTills = () => {
        setLoading(true);
        tillEndpoints
            .clearTills()
            .then(() => {
                return loadCurrentAssignments();
            })
            .catch((err) => {
                const description = describeNetworkError(err).join("\n");
                logger.error(
                    "Failed to clear assignments or load current assignments.",
                    {
                        errorMessage: description,
                    },
                );
                setClearTillsErrorMessage(description);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const getIsDisabled = (tillNumber: number) => {
        return unavailableTills.indexOf(tillNumber) !== -1;
    };

    const completeSkipTillAssignment = (
        managerValidationRes: ValidateManagerResponse,
    ) => {
        if (!noTillSelection.current) {
            return;
        }
        setManagerPermissionsDrawerOpen(false);

        if (noTillSelection.current === "no-till-override") {
            return dispatch(
                posActions.selectNoTillOverride(managerValidationRes.id),
            ).then(() => {
                navigate(
                    stationMode === "drive_thru_order_fulfillment"
                        ? routeHelpers.otherOrders()
                        : routeHelpers.menu(),
                );
            });
        }

        setLoading(true);
        dispatch(
            posActions.editStationMode(
                noTillSelection.current === "drive-thru"
                    ? "drive_thru_order_taker"
                    : "line_buster",
                "lane_1",
            ),
        )
            .then(() => {
                dispatch(
                    Actions.currentOrderActions.selectOrderType("drive_thru"),
                );
                if (!posCurrentOrder.id) {
                    dispatch(posActions.reserveCurrentOrderId());
                }
                dispatch(posActions.setTillAssignmentSkipped(true));
                navigate(
                    driveThruMenuDefaultCategoryId
                        ? routeHelpers.category(driveThruMenuDefaultCategoryId)
                        : routeHelpers.menu(),
                );
            })
            .catch(() => {
                setStationModeChangeErrored(true);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // we only need to perform the initial load when this component is mounted if there is no assigned till.
    useEffect(() => {
        if (
            (!isTraining && assignedTill === -1) ||
            (isTraining && trainingModeAssignedTill === -1)
        ) {
            loadCurrentAssignments();
        }
    }, []);

    // once a till has been assigned, we need to navigate to the correct screen based on the station mode
    useEffect(() => {
        if (
            (!isTraining && assignedTill !== -1) ||
            (isTraining && trainingModeAssignedTill !== -1)
        ) {
            if (stationMode === "drive_thru_order_fulfillment") {
                navigate(routeHelpers.otherOrders());
            } else if (defaultCategoryId) {
                navigate(routeHelpers.category(defaultCategoryId));
            }
        }
    }, [
        assignedTill,
        prevAssignedTill,
        stationMode,
        defaultCategoryId,
        isTraining,
        trainingModeAssignedTill,
        prevTrainingAssignedTill,
    ]);

    if (loading) {
        return (
            <div className={styles.container}>
                <ManagerHeader label={strings.title} />
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            </div>
        );
    }

    if (isTraining && trainingModeAssignedTill !== -1) {
        return null;
    }

    if ((!isTraining && assignedTill !== -1) || tillAssignmentSkipped) {
        return null;
    }

    return (
        <>
            <div className={styles.container}>
                <ManagerHeader
                    label={strings.title}
                    buttonLabel={strings.refreshTills}
                    buttonOnClick={loadCurrentAssignments}
                />
                <div className={styles.bodyContainer}>
                    {isLab ? (
                        <OutlineButton
                            className={styles.clearTillsButton}
                            label="Clear Tills"
                            onClick={clearTills}
                        />
                    ) : null}
                    <img className={styles.image} src={tillIcon} alt="" />

                    {!loadAssignmentsErrored ? (
                        <>
                            <div className={styles.mainInstruction}>
                                {strings.mainInstruction}
                            </div>
                            <div className={styles.instruction}>
                                {strings.instructions}
                            </div>
                            <div className={styles.optionContainer}>
                                {Array(numTills)
                                    .fill(0)
                                    .map((_, i) => {
                                        const tillNumber = i + 1;
                                        const isTillSelected =
                                            tillToAssign === tillNumber;
                                        return (
                                            <ToggleButton
                                                key={i}
                                                className={cn(
                                                    styles.tillOption,
                                                    numTills === 6
                                                        ? styles.tillOption6
                                                        : styles.tillOption4,
                                                )}
                                                onChange={() =>
                                                    setTillToAssign(tillNumber)
                                                }
                                                label={tillNumber.toString()}
                                                checked={isTillSelected}
                                                disabled={getIsDisabled(
                                                    tillNumber,
                                                )}
                                            />
                                        );
                                    })}
                            </div>
                            <div className={styles.noTillsLabel}>
                                {strings.modesWithoutTill}
                            </div>
                            <div className={styles.noTillsContainer}>
                                <OutlineButton
                                    className={styles.skipAssignmentButton}
                                    label={strings.skipAssignmentDriveThru}
                                    labelClassName={
                                        styles.skipAssignmentButtonText
                                    }
                                    mode="blue"
                                    onClick={() => {
                                        noTillSelection.current = "drive-thru";
                                        setManagerPermissionsDrawerOpen(true);
                                    }}
                                />
                                <OutlineButton
                                    className={styles.skipAssignmentButton}
                                    label={strings.skipAssignmentLineBuster}
                                    labelClassName={
                                        styles.skipAssignmentButtonText
                                    }
                                    mode="blue"
                                    onClick={() => {
                                        noTillSelection.current = "line_buster";
                                        setManagerPermissionsDrawerOpen(true);
                                    }}
                                />
                                <OutlineButton
                                    className={styles.skipAssignmentButton}
                                    label={strings.noTillOverride}
                                    labelClassName={
                                        styles.skipAssignmentButtonText
                                    }
                                    mode="blue"
                                    onClick={() => {
                                        noTillSelection.current =
                                            "no-till-override";
                                        setManagerPermissionsDrawerOpen(true);
                                    }}
                                />
                            </div>
                            <div className={styles.bottom}>
                                <div>
                                    <div className={styles.noticeText}>
                                        {strings.noticeTitle}
                                    </div>
                                    <div className={styles.noticeText}>
                                        {strings.noticeInstruction}
                                    </div>
                                </div>
                                <OutlineButton
                                    label={strings.assign}
                                    onClick={() => {
                                        dispatch(
                                            posActions.assignTill(tillToAssign),
                                        ).then(() => {
                                            if (
                                                stationMode ===
                                                "drive_thru_order_fulfillment"
                                            ) {
                                                navigate(
                                                    routeHelpers.otherOrders(),
                                                );
                                            }
                                        });
                                    }}
                                    className={styles.assignButton}
                                    disabled={tillToAssign === -1}
                                    loading={isAssigningTill}
                                />
                            </div>
                        </>
                    ) : (
                        <div>
                            <div className={styles.loadError}>
                                {strings.loadAssignmentsErrorMessage}
                            </div>
                            <div className={styles.retryLoadButtonContainer}>
                                <OutlineButton
                                    label={strings.retry}
                                    onClick={loadCurrentAssignments}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {tillAssignmentErrorMessage || stationModeChangeErrored ? (
                <ErrorModal
                    errorMessage={
                        stationModeChangeErrored
                            ? strings.stationModeChangeError
                            : (tillAssignmentErrorMessage as string)
                    }
                    onClose={() => {
                        if (tillAssignmentErrorMessage) {
                            dispatch(
                                posActions.setTillAssignmentErrorMessage(
                                    undefined,
                                ),
                            );
                            // error could have happened because we tried to assign a till that was assigned to a different one before we refetched current assignments.
                            loadCurrentAssignments();
                        } else {
                            setStationModeChangeErrored(false);
                        }
                    }}
                    showITInfo
                />
            ) : null}
            {clearTillsErrorMessage ? (
                <ErrorModal
                    errorMessage={clearTillsErrorMessage}
                    onClose={() => {
                        setClearTillsErrorMessage("");
                    }}
                    showITInfo
                />
            ) : null}
            {managerPermissionsDrawerOpen ? (
                <Drawer
                    size="large"
                    onClose={() => {
                        setManagerPermissionsDrawerOpen(false);
                    }}>
                    <div className={styles.managerPermissionFormContainer}>
                        <ManagerPermissionForm
                            onSuccess={completeSkipTillAssignment}
                        />
                    </div>
                </Drawer>
            ) : null}
        </>
    );
}
